import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router";
import {
  ACTIVITY_PAGE_SIZE,
  ADMIN_PAGE_SIZE,
  createPlatform,
  fetchAdminActivity,
  fetchAdmins,
  inviteAdmin,
  resetAdminPassword,
  type AdminAccount,
  type TemporaryPassword,
  deleteParticipant,
  downloadExport,
  fetchAdminPlatforms,
  fetchFinanceDashboard,
  fetchGigDashboard,
  fetchRosterSize,
  importRoster,
  updatePlatform,
  type AdminPlatform,
  type ExportKind,
  type FinanceDashboard,
  type GigDashboard,
} from "../../api/admin";
import { changePassword } from "../../api/auth";
import { fetchContent } from "../../api/content";
import { AdminShell } from "../../components/admin/AdminShell";
import { ToolbarButton } from "../../components/admin/AdminUi";
import { ParticipantPanel } from "../../components/admin/ParticipantPanel";
import { Banner } from "../../components/Banner";
import { ScreenState } from "../../components/ScreenState";
import { attempt, messageOf } from "../../lib/errors";
import { useSession } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { useDebouncedCallback } from "../../lib/useDebounced";
import {
  FinanceDashboardScreen,
  type FinanceFilterState,
} from "../../screens/admin/FinanceDashboardScreen";
import { GigDashboardScreen, type GigFilterState } from "../../screens/admin/GigDashboardScreen";
import { PlatformsScreen } from "../../screens/admin/PlatformsScreen";
import { RosterScreen } from "../../screens/admin/RosterScreen";
import { AccountScreen } from "../../screens/admin/AccountScreen";
import { ActivityScreen, type ActivityFilterState } from "../../screens/admin/ActivityScreen";
import { ADMIN_ROLE_LABEL, TeamScreen } from "../../screens/admin/TeamScreen";

/* Admins land on the first dashboard their role opens. */
export function AdminHomeRoute() {
  const { user } = useSession();
  if (!user) return null;
  return <Navigate to={user.role === "FinanceAdmin" ? "/admin/finance" : "/admin/gig"} replace />;
}

/*
 * CSV exports for the weekly LMS reconciliation. These are reports the admin team builds, not participant documents,
 * so they stay downloadable; participant CVs and screenshots are view-only (see FileViewer).
 */
function ExportMenu({ kinds }: { kinds: [ExportKind, string][] }) {
  const [busy, setBusy] = useState<ExportKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      {kinds.map(([kind, label]) => (
        <ToolbarButton
          key={kind}
          disabled={busy !== null}
          onClick={() => {
            setBusy(kind);
            setError(null);
            downloadExport(kind)
              .catch((e: unknown) => setError(messageOf(e)))
              .finally(() => setBusy(null));
          }}
        >
          {busy === kind ? "Preparing..." : label}
        </ToolbarButton>
      ))}
      {error ? (
        <span role="alert" className="self-center text-[13px] text-negative">
          {error}
        </span>
      ) : null}
    </>
  );
}

/* Shared list state: filters (search debounced), page, and the open participant. */
function useListState<F extends { search: string }>(initial: F) {
  const [filter, setFilter] = useState(initial);
  const [query, setQuery] = useState(initial);
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const searchSoon = useDebouncedCallback((next: F) => setQuery(next), 300);
  const onFilter = (patch: Partial<F>) => {
    const next = { ...filter, ...patch };
    setFilter(next);
    setPage(0);
    if ("search" in patch && Object.keys(patch).length === 1) searchSoon(next);
    else setQuery(next);
  };
  return { filter, query, page, setPage, openId, setOpenId, onFilter };
}

/*
 * Keeps the last good data on screen while a new page or filter loads, so the table dims instead of flashing a
 * skeleton on every click.
 */
function useStickyData<T>(load: () => Promise<T>, deps: unknown[]) {
  const loaded = useAsync(load, deps);
  const [last, setLast] = useState<T | null>(null);
  useEffect(() => {
    if (loaded.data) setLast(loaded.data);
  }, [loaded.data]);
  return { ...loaded, data: loaded.data ?? last };
}

function useDelete(reload: () => void, close: () => void) {
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    (userId: string, email: string) => {
      if (!window.confirm(`Permanently delete ${email} and all their data? This can't be undone.`))
        return;
      void attempt(
        () => undefined,
        setError,
        async () => {
          await deleteParticipant(userId);
          close();
          reload();
        },
      );
    },
    [reload, close],
  );
  return { error, run };
}

export function GigDashboardRoute() {
  const { user } = useSession();
  const list = useListState<GigFilterState>({
    search: "",
    track: "",
    stage: "",
    stalledOnly: false,
  });
  const { query, page } = list;
  const loaded = useStickyData<GigDashboard>(
    () =>
      fetchGigDashboard({
        search: query.search,
        track: query.track || null,
        gigStage: query.stage || null,
        stalledOnly: query.stalledOnly,
        limit: ADMIN_PAGE_SIZE,
        pageNumber: page,
      }),
    [query, page],
  );
  const close = useCallback(() => list.setOpenId(null), [list]);
  const del = useDelete(loaded.reload, close);

  return (
    <AdminShell
      title="Gig readiness"
      actions={
        <ExportMenu
          kinds={[
            ["gig", "Export CSV"],
            ["gig-gate-log", "Export gate log"],
          ]}
        />
      }
    >
      {del.error ? <Banner tone="bad">{del.error}</Banner> : null}
      <ScreenState
        loading={loaded.loading && !loaded.data}
        error={loaded.data ? null : loaded.error}
        onRetry={loaded.reload}
      >
        <GigDashboardScreen
          data={loaded.data}
          loading={loaded.loading}
          filter={list.filter}
          page={page}
          selectedId={list.openId}
          onFilter={list.onFilter}
          onPage={list.setPage}
          onOpen={(row) => list.setOpenId(row.userId)}
        />
      </ScreenState>
      {list.openId ? (
        <ParticipantPanel
          userId={list.openId}
          focus="gig"
          canDelete={user?.role === "SuperAdmin"}
          onClose={close}
          onDelete={del.run}
        />
      ) : null}
    </AdminShell>
  );
}

export function FinanceDashboardRoute() {
  const { user } = useSession();
  const list = useListState<FinanceFilterState>({ search: "", stage: "" });
  const { query, page } = list;
  const content = useAsync(fetchContent, []);
  const loaded = useStickyData<FinanceDashboard>(
    () =>
      fetchFinanceDashboard({
        search: query.search,
        financeStage: query.stage || null,
        limit: ADMIN_PAGE_SIZE,
        pageNumber: page,
      }),
    [query, page],
  );
  const close = useCallback(() => list.setOpenId(null), [list]);
  const del = useDelete(loaded.reload, close);

  return (
    <AdminShell
      title="Access to Finance"
      actions={
        <ExportMenu
          kinds={[
            ["finance", "Export CSV"],
            ["finance-gate-log", "Export gate log"],
          ]}
        />
      }
    >
      {del.error ? <Banner tone="bad">{del.error}</Banner> : null}
      <ScreenState
        loading={loaded.loading && !loaded.data}
        error={loaded.data ? null : loaded.error}
        onRetry={loaded.reload}
      >
        <FinanceDashboardScreen
          data={loaded.data}
          loading={loaded.loading}
          filter={list.filter}
          page={page}
          selectedId={list.openId}
          idDocsTotal={content.data?.idDocs.length ?? 7}
          moneyDocsTotal={content.data?.moneyDocs.filter((d) => !d.optional).length ?? 5}
          onFilter={list.onFilter}
          onPage={list.setPage}
          onOpen={(row) => list.setOpenId(row.userId)}
        />
      </ScreenState>
      {list.openId ? (
        <ParticipantPanel
          userId={list.openId}
          focus="finance"
          canDelete={user?.role === "SuperAdmin"}
          onClose={close}
          onDelete={del.run}
        />
      ) : null}
    </AdminShell>
  );
}

export function PlatformsRoute() {
  const loaded = useAsync(fetchAdminPlatforms, []);
  const [platforms, setPlatforms] = useState<AdminPlatform[]>([]);
  const [editing, setEditing] = useState<AdminPlatform | "new" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setPlatforms(loaded.data);
  }, [loaded.data]);

  return (
    <AdminShell
      title="Partner platforms"
      actions={
        <ToolbarButton tone="primary" onClick={() => setEditing("new")}>
          + Add platform
        </ToolbarButton>
      }
    >
      <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
        <PlatformsScreen
          platforms={platforms}
          busy={busy}
          message={message}
          error={error}
          editing={editing}
          onEdit={(next) => {
            setError(null);
            setEditing(next);
          }}
          onSave={(edit) =>
            void attempt(setBusy, setError, async () => {
              const saved = await updatePlatform(edit);
              setPlatforms((current) => current.map((p) => (p.id === saved.id ? saved : p)));
              setMessage(`${saved.name} saved.`);
              setEditing(null);
            })
          }
          onCreate={(platform) =>
            void attempt(setBusy, setError, async () => {
              const created = await createPlatform(platform);
              setPlatforms((current) => [...current, created]);
              setMessage(`${created.name} added.`);
              setEditing(null);
            })
          }
        />
      </ScreenState>
    </AdminShell>
  );
}

export function RosterRoute() {
  const loaded = useAsync(fetchRosterSize, []);
  const [size, setSize] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data != null) setSize(loaded.data);
  }, [loaded.data]);

  return (
    <AdminShell title="Participant roster">
      <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
        <RosterScreen
          size={size}
          busy={busy}
          message={message}
          error={error}
          onImport={(entries, replace) =>
            void attempt(setBusy, setError, async () => {
              const next = await importRoster(entries, replace);
              setSize(next);
              setMessage(`Roster loaded. ${next} entries on the roster.`);
            })
          }
        />
      </ScreenState>
    </AdminShell>
  );
}

export function ActivityRoute() {
  const list = useListState<ActivityFilterState>({ search: "", action: "" });
  const { query, page } = list;
  const loaded = useStickyData(
    () =>
      fetchAdminActivity({
        search: query.search,
        action: query.action || null,
        pageNumber: page,
      }),
    [query, page],
  );

  return (
    <AdminShell
      title="Activity"
      actions={<ToolbarButton onClick={loaded.reload}>Refresh</ToolbarButton>}
    >
      <ScreenState
        loading={loaded.loading && !loaded.data}
        error={loaded.data ? null : loaded.error}
        onRetry={loaded.reload}
      >
        <ActivityScreen
          rows={loaded.data?.rows ?? []}
          total={loaded.data?.total ?? 0}
          loading={loaded.loading}
          page={page}
          pageSize={ACTIVITY_PAGE_SIZE}
          filter={list.filter}
          onFilter={list.onFilter}
          onPage={list.setPage}
        />
      </ScreenState>
    </AdminShell>
  );
}

export function AccountRoute() {
  const { user, setUser } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  if (!user || user.role === "Participant") return null;

  return (
    <AdminShell title="My account">
      <AccountScreen
        key={user.mustChangePassword ? "must" : "normal"}
        name={`${user.firstName} ${user.lastName}`.trim()}
        email={user.email}
        roleLabel={ADMIN_ROLE_LABEL[user.role]}
        mustChange={Boolean(user.mustChangePassword)}
        busy={busy}
        error={error}
        message={message}
        onChangePassword={(current, next) =>
          void attempt(setBusy, setError, async () => {
            setMessage(null);
            setUser(await changePassword(current, next));
            setMessage("Password changed. You've been signed out on every other device.");
          })
        }
      />
    </AdminShell>
  );
}

export function TeamRoute() {
  const { user } = useSession();
  const loaded = useAsync(fetchAdmins, []);
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{
    password: TemporaryPassword;
    reason: "added" | "reset";
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canManage = user?.role === "SuperAdmin";

  useEffect(() => {
    if (loaded.data) setAdmins(loaded.data);
  }, [loaded.data]);

  const upsert = (account: AdminAccount) =>
    setAdmins((current) =>
      current.some((a) => a.id === account.id)
        ? current.map((a) => (a.id === account.id ? account : a))
        : [...current, account],
    );

  return (
    <AdminShell
      title="Admin team"
      actions={
        canManage ? (
          <ToolbarButton
            tone="primary"
            onClick={() => {
              setError(null);
              setAdding(true);
            }}
          >
            + Add admin
          </ToolbarButton>
        ) : undefined
      }
    >
      <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
        <TeamScreen
          admins={admins}
          currentUserId={user?.id ?? ""}
          canManage={canManage}
          busy={busy}
          error={error}
          adding={adding}
          issued={issued}
          onAdding={setAdding}
          onInvite={(input) =>
            void attempt(setBusy, setError, async () => {
              const result = await inviteAdmin(input);
              upsert(result.user);
              setAdding(false);
              setIssued({ password: result, reason: "added" });
            })
          }
          onReset={(admin) => {
            if (
              !window.confirm(
                `Reset the password for ${admin.email}? They'll be signed out everywhere and get a one-time password from you.`,
              )
            )
              return;
            void attempt(setBusy, setError, async () => {
              const result = await resetAdminPassword(admin.id);
              upsert(result.user);
              setIssued({ password: result, reason: "reset" });
            });
          }}
          onDismissIssued={() => setIssued(null)}
        />
      </ScreenState>
    </AdminShell>
  );
}
